import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useField, useFormikContext } from 'formik';
import { FormSimpleAutoComplete } from '../index';
import { Filter, ListParams } from '../../types/main';
import UserService from '../../services/UserService';
import { User } from '../../types/items';

type Props = {
  userId?: number;
  organizationId?: number;
  optionalUserId?: number;
  additionalFilters?: Filter[];
  disabled?: boolean;
  name?: string;
  label?: string;
  userIdsFilter?: number[];
  defaultValue?: null | string | number;
};

const UserField = ({
  userId,
  optionalUserId,
  organizationId,
  additionalFilters = [],
  disabled = false,
  name = 'user_id',
  label = 'Referring User',
  userIdsFilter,
  defaultValue = null,
}: Props) => {
  const dispatch = useDispatch();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const { setFieldValue } = useFormikContext();
  const fieldValue = useField({ name })[0].value;

  const params: ListParams = {
    page_size: 10,
    sort_key: 'ref.full_name_first_last',
    sort_dir: 'ASC',
  };

  const getUsers = useCallback(async (listParams, initialCall = false) => {
    setLoading(true);
    const usersParams = { ...listParams };
    const filters = usersParams.filter || {};

    if (additionalFilters) {
      additionalFilters.forEach((filter) => {
        filters[filter.key] = filter.value;
      });
    }

    if (organizationId && organizationId > 0) {
      filters.organization_id = organizationId;
    } else if (optionalUserId) {
      filters.user_id = optionalUserId;
    }
    usersParams.filter = filters;
    const fetchUsers = await UserService.list(usersParams);
    setUsers(fetchUsers.items);
    if (!initialCall && !fetchUsers.items.find((item) => item.id === fieldValue)) {
      setFieldValue(name as never, defaultValue);
    }
    setLoading(false);
  }, [
    setUsers,
    setLoading,
    organizationId,
    setFieldValue,
    name,
    fieldValue,
    optionalUserId,
    additionalFilters,
    defaultValue,
  ]);

  const searchUsers = useCallback((search) => {
    getUsers({ ...params, filter: { search } });
  }, [dispatch, getUsers]);

  const filteredUsers = userIdsFilter?.length
    ? users.filter((user) => !userIdsFilter.includes(user.id))
    : users;

  const usersList = filteredUsers?.map((item) => ({
    label: item.ref.full_name_first_last,
    id: item.id,
  }));

  // Init call
  /* eslint react-hooks/exhaustive-deps: 0 */
  useEffect(() => {
    const firstCallParams = { ...params };

    if (userId && userId > 0) {
      firstCallParams.filter = { user_id: userId.toString() };
    }

    getUsers(firstCallParams, true);
  }, [dispatch]);

  return (
    <FormSimpleAutoComplete
      options={usersList}
      label={label}
      name={name}
      onSearch={searchUsers}
      loading={loading}
      disabled={disabled}
    />
  );
};


export default UserField;
